import { VerifyEvidence, VerifyOutcome } from './verify/types';

export const ChatMode = {
  CLIENT: 'CLIENT',
  DOCUMENT: 'DOCUMENT',
  FAMILY: 'FAMILY',
} as const;

export type ChatMode = (typeof ChatMode)[keyof typeof ChatMode];

export const ChatState = {
  THINKING: 'THINKING',
  ANSWERING: 'ANSWERING',
  EDITING: 'EDITING',
  FINAL: 'FINAL',
  CANCELLED: 'CANCELLED',
  TOOL_STARTED: 'TOOL_STARTED',
  TOOL_FINISHED: 'TOOL_FINISHED',
  TOOL_LIMIT_REACHED: 'TOOL_LIMIT_REACHED',
  FILE_PRESENTED: 'FILE_PRESENTED',
  DATA_UPDATED: 'DATA_UPDATED',
  ACTION_PROPOSED: 'ACTION_PROPOSED',
  RETRY_NOTICE: 'RETRY_NOTICE',
  RESTARTED: 'RESTARTED',
} as const;

export type ChatState = (typeof ChatState)[keyof typeof ChatState];

export type FinalChunkData = {
  conversationId: string;
  editedContent: string;
  editedContentWithMarkers?: string | null;
  assistantMessage: string;
  versionId: string | null;
  versionNumber: number | null;
  versioningFailed: boolean;
  evidence?: VerifyEvidence[];
  verifyOutcome?: VerifyOutcome;
  verifyNote?: string;
};

export enum MessageDirection {
  Incoming = 'incoming',
  Outgoing = 'outgoing',
}

export type MessageState = 'SENT' | 'ANSWERING' | 'THINKING' | string;

export type MessageContent = { state: MessageState; content: string };

export type PresentedFile = { description: string; url: string };

export type ScenarioUpdate = {
  domain: string;
  clientId: string;
  scenarioId: string;
  // Set for family-scoped models; their detail page lives on the family route.
  familyId?: string;
  phase: 'committed' | 'ran' | 'reverted' | string;
  headline: string;
};

export type ProposedActionStatus = 'PENDING' | 'APPLIED' | 'DENIED' | 'FAILED' | 'SUPERSEDED';

export type ProposedAction = {
  proposalId: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  templateId?: string | null;
  templateName: string;
  templateType: 'MEETING_NOTE' | 'MEETING_AGENDA';
  summary: string;
  changeStats?: {
    sectionsAdded: number;
    sectionsRemoved: number;
    sectionsModified: number;
  } | null;
  status?: ProposedActionStatus;
  failureReason?: string | null;
};

export type MessageAttachment = { filename: string; extension: string };

export type ChatMessage = {
  kind: 'message';
  id: string;
  response: MessageContent[];
  sender: string;
  direction: MessageDirection;
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
  currentToolName?: string;
  retryNotice?: string;
  presentedFiles?: PresentedFile[];
  scenarioUpdates?: ScenarioUpdate[];
  proposedActions?: ProposedAction[];
  attachments?: MessageAttachment[];
  // Response length at the last completed tool call; RESTARTED rewinds to here.
  restartCheckpointIndex?: number;
  verifyOutcome?: VerifyOutcome;
  evidence?: VerifyEvidence[];
  verifyMeetingNoteId?: string;
  verifyNote?: string;
};

type InterruptedMarker = {
  kind: 'interrupted';
  id: string;
};

// A turn whose background worker never returned: the stream ended without an answer, so we mark
// the gap rather than leaving the question looking unanswered or spinning forever.
type FailedMarker = {
  kind: 'failed';
  id: string;
};

export type Message = ChatMessage | InterruptedMarker | FailedMarker;

export const isChatMessage = (m: Message): m is ChatMessage => m.kind === 'message';

export type StoredMessage = {
  role: string;
  content?: string | null;
  attachments?: MessageAttachment[];
};
